import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import PageBreadcrumb from '../../components/common/PageBreadCrumb';
import ComponentCard from '../../components/common/ComponentCard';
import PageMeta from '../../components/common/PageMeta';
import { Table, TableBody, TableCell, TableHeader, TableRow } from '../../components/ui/table';
import dayjs from 'dayjs';
import { Family, useFamilyStore } from '../../store/useFamilyStore';
import { useUnitStore } from '../../store/useUnitStore';
import { Button, Label, Modal, Popover, TextInput, ToggleSwitch, Select } from 'flowbite-react';
import { HiOutlineExclamationCircle, HiX, HiPlus } from 'react-icons/hi';
import toast from 'react-hot-toast';
import { useCanAccessResource, useHasPermission, useIsMainAdmin } from '../../utils/permissions';
import { usePermissionStore } from '../../store/usePermissionStore';
import AccessDenied from '../../components/common/AccessDenied';

interface FamilyMemberForm {
  name: string;
  dob: string;
  education: string;
  job: string;
  baptismDate: string;
  firstCommunionDate: string;
  confirmationDate: string;
  married: boolean;
  marriedDate: string;
  deathDate: string;
  mother: string;
  father: string;
  contactNumbers: string[];
}

export default function FamiliesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const getFamilies = useFamilyStore((state) => state.getFamilies);
  const deleteFamily = useFamilyStore((state) => state.deleteFamily);
  const families = useFamilyStore((state) => state.families);
  const createFamily = useFamilyStore((state) => state.createFamily);
  const updateFamily = useFamilyStore((state) => state.updateFamily);
  const loading = useFamilyStore((state) => state.loading);
  const pagination = useFamilyStore((state) => state.pagination);
  const getUnits = useUnitStore((state) => state.getUnits);
  const units = useUnitStore((state) => state.units);
  
  const canAccess = useCanAccessResource('families');
  const hasReadPermission = useHasPermission('families', 'read');
  const hasCreatePermission = useHasPermission('families', 'create');
  const hasUpdatePermission = useHasPermission('families', 'update');
  const hasDeletePermission = useHasPermission('families', 'delete');
  const isMainAdmin = useIsMainAdmin();
  const isLoadingPermissions = usePermissionStore((state) => state.isLoading);
  const hasFetched = usePermissionStore((state) => state.hasFetched);

  const [tableData, setTableData] = useState<Family[]>([]);
  const [search, setSearch] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit] = useState<number>(10);
  const [openModal, setOpenModal] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState<string | null>(null);
  const [openAddModal, setOpenAddModal] = useState(false);
  const [modalType, setModalType] = useState<string>('add');
  const [editId, setEditId] = useState<string | null>(null);

  const [familyName, setFamilyName] = useState('');
  const [selectedUnit, setSelectedUnit] = useState<string>('');
  const [guardianInfo, setGuardianInfo] = useState<FamilyMemberForm>({
    name: '',
    dob: '',
    education: '',
    job: '',
    baptismDate: '',
    firstCommunionDate: '',
    confirmationDate: '',
    married: false,
    marriedDate: '',
    deathDate: '',
    mother: '',
    father: '',
    contactNumbers: [],
  });
  const [familyMembers, setFamilyMembers] = useState<FamilyMemberForm[]>([
    {
      name: '',
      dob: '',
      education: '',
      job: '',
      baptismDate: '',
      firstCommunionDate: '',
      confirmationDate: '',
      married: false,
      marriedDate: '',
      deathDate: '',
      mother: '',
      father: '',
      contactNumbers: [],
    },
  ]);

  useEffect(() => {
    getUnits();
  }, [getUnits]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  useEffect(() => {
    // Wait for permissions to load before checking
    if (hasFetched && !isLoadingPermissions) {
      // Only fetch if user has read permission
      if (hasReadPermission || isMainAdmin) {
        getFamilies(search || undefined, currentPage, limit);
      }
    }
  }, [getFamilies, search, currentPage, limit, canAccess, hasReadPermission, isMainAdmin, hasFetched, isLoadingPermissions]);

  useEffect(() => {
    setTableData(families);
  }, [families]);

  // Handle pre-selected unit from navigation state
  useEffect(() => {
    const preselectedUnit = (location.state as any)?.preselectedUnit;
    if (preselectedUnit && units.length > 0) {
      setSelectedUnit(preselectedUnit);
      setOpenAddModal(true);
      setModalType('add');
      // Clear the navigation state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state, units]);

  const handleAddOrEdit = async () => {
    // Check permissions
    if (modalType === 'add' && !hasCreatePermission && !isMainAdmin) {
      toast.error('You don\'t have permission to create families');
      return;
    }
    if (modalType === 'edit' && !hasUpdatePermission && !isMainAdmin) {
      toast.error('You don\'t have permission to update families');
      return;
    }

    if (!familyName || !selectedUnit) {
      toast.error('Please fill in all required fields (Family Name and Unit).', {
        duration: 2000,
        icon: '❌',
      });
      return;
    }

    // Householder is mandatory - check if householder name is provided
    if (!guardianInfo.name.trim()) {
      toast.error('Householder Information is required. Please fill in at least the Householder Name.', {
        duration: 2000,
        icon: '❌',
      });
      return;
    }

    // Filter out empty members (at least name should be filled) - members are optional
    const validMembers = familyMembers.filter((m) => m.name.trim() !== '');

    // Filter out empty contact numbers for guardian and members
    const guardianData = {
      ...guardianInfo,
      contactNumbers: guardianInfo.contactNumbers.filter((cn) => cn.trim() !== ''),
    };
    const membersWithFilteredContacts = validMembers.map((m) => ({
      ...m,
      contactNumbers: m.contactNumbers.filter((cn) => cn.trim() !== ''),
    }));

    if (modalType === 'add') {
      await createFamily({
        familyName,
        unit: selectedUnit,
        guardian: guardianData,
        members: membersWithFilteredContacts.length > 0 ? membersWithFilteredContacts : undefined,
      });
    } else {
      if (!editId) return;
      await updateFamily(editId, {
        familyName,
        unit: selectedUnit,
      });
    }

    setOpenAddModal(false);
    setFamilyName('');
    setSelectedUnit('');
    setGuardianInfo({
      name: '',
      dob: '',
      education: '',
      job: '',
      baptismDate: '',
      firstCommunionDate: '',
      confirmationDate: '',
      married: false,
      marriedDate: '',
      deathDate: '',
      mother: '',
      father: '',
      contactNumbers: [],
    });
    setFamilyMembers([
      {
        name: '',
        dob: '',
        education: '',
        job: '',
        baptismDate: '',
        firstCommunionDate: '',
        confirmationDate: '',
        married: false,
        marriedDate: '',
        deathDate: '',
        mother: '',
        father: '',
        contactNumbers: [],
      },
    ]);
    setEditId(null);
    setModalType('add');
  };

  const handleEdit = (family: Family) => {
    if (!hasUpdatePermission && !isMainAdmin) {
      toast.error('You don\'t have permission to edit families');
      return;
    }
    setEditId(family._id);
    setModalType('edit');
    setOpenAddModal(true);
    setFamilyName(family.familyName);
    const unitId = typeof family.unit === 'string' ? family.unit : family.unit?._id || '';
    setSelectedUnit(unitId);
  };

  const handleDelete = () => {
    if (!hasDeletePermission && !isMainAdmin) {
      toast.error('You don\'t have permission to delete families');
      setOpenModal(false);
      setSelectedFamily(null);
      return;
    }
    if (selectedFamily !== null) {
      deleteFamily(selectedFamily);
      setOpenModal(false);
      setSelectedFamily(null);
    }
  };

  const addMember = () => {
    setFamilyMembers([
      ...familyMembers,
      {
        name: '',
        dob: '',
        education: '',
        job: '',
        baptismDate: '',
        firstCommunionDate: '',
        confirmationDate: '',
        married: false,
        marriedDate: '',
        deathDate: '',
        mother: '',
        father: '',
        contactNumbers: [''],
      },
    ]);
  };

  const removeMember = (index: number) => {
    setFamilyMembers(familyMembers.filter((_, i) => i !== index));
  };

  const updateMember = (index: number, field: keyof FamilyMemberForm, value: any) => {
    const updated = [...familyMembers];
    updated[index] = { ...updated[index], [field]: value };
    setFamilyMembers(updated);
  };

  const addContactNumber = (memberIndex: number) => {
    const updated = [...familyMembers];
    updated[memberIndex].contactNumbers.push('');
    setFamilyMembers(updated);
  };

  const removeContactNumber = (memberIndex: number, contactIndex: number) => {
    const updated = [...familyMembers];
    updated[memberIndex].contactNumbers = updated[memberIndex].contactNumbers.filter((_, i) => i !== contactIndex);
    setFamilyMembers(updated);
  };

  const updateContactNumber = (memberIndex: number, contactIndex: number, value: string) => {
    const updated = [...familyMembers];
    updated[memberIndex].contactNumbers[contactIndex] = value;
    setFamilyMembers(updated);
  };

  // Show loading while permissions are being fetched
  if (isLoadingPermissions || !hasFetched) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="text-lg text-gray-600">Loading permissions...</div>
        </div>
      </div>
    );
  }

  // Show permission denied message if no access (but don't redirect)
  if (!canAccess && !isMainAdmin) {
    return <AccessDenied resourceName="Families" />;
  }

  return (
    <>
      <Modal
        show={openModal}
        size="md"
        position="center"
        onClose={() => {
          setOpenModal(false);
          setSelectedFamily(null);
        }}
        popup
      >
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
              Are you sure you want to delete this family? This will also delete all family members.
            </h3>
            <div className="flex justify-center gap-4">
              <Button
                onClick={handleDelete}
                className="px-6 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                Yes, I'm sure
              </Button>
              <Button
                onClick={() => {
                  setOpenModal(false);
                  setSelectedFamily(null);
                }}
                className="px-6 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                No, cancel
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <Modal
        show={openAddModal}
        size="6xl"
        popup
        onClose={() => {
          setOpenAddModal(false);
          setFamilyName('');
          setSelectedUnit('');
          setGuardianInfo({
            name: '',
            dob: '',
            education: '',
            job: '',
            baptismDate: '',
            firstCommunionDate: '',
            confirmationDate: '',
            married: false,
            marriedDate: '',
            deathDate: '',
            mother: '',
            father: '',
            contactNumbers: [],
          });
          setFamilyMembers([
            {
              name: '',
              dob: '',
              education: '',
              job: '',
              baptismDate: '',
              firstCommunionDate: '',
              confirmationDate: '',
              married: false,
              marriedDate: '',
              deathDate: '',
              mother: '',
              father: '',
              contactNumbers: [],
            },
          ]);
          setEditId(null);
          setModalType('add');
        }}
      >
        <Modal.Header />
        <Modal.Body>
          <div className="space-y-6 max-h-[85vh] overflow-y-auto">
            <h3 className="text-xl font-medium text-gray-900 dark:text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
              {modalType === 'add' ? 'Add Family' : 'Edit Family'}
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="mb-2 block">
                  <Label htmlFor="familyName" value="Family Name *" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <TextInput
                  id="familyName"
                  type="text"
                  placeholder="Enter family name"
                  value={familyName}
                  onChange={(e) => setFamilyName(e.target.value)}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                />
              </div>

              <div>
                <div className="mb-2 block">
                  <Label htmlFor="unit" value="Unit *" style={{ fontFamily: 'Lato, sans-serif' }} />
                </div>
                <Select id="unit" value={selectedUnit} onChange={(e) => setSelectedUnit(e.target.value)} style={{ fontFamily: 'Lato, sans-serif' }}>
                  <option value="">Select Unit</option>
                  {units.map((unit) => (
                    <option key={unit._id} value={unit._id}>
                      {unit.name} ({unit.unitNumber})
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {modalType === 'add' && (
              <>
                <div className="border-t pt-6">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                    Householder Information *
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4" style={{ fontFamily: 'Lato, sans-serif' }}>
                    Add information about the family householder (required)
                  </p>

                  <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="guardian-name" value="Name *" style={{ fontFamily: 'Lato, sans-serif' }} />
                        <TextInput
                          id="guardian-name"
                          value={guardianInfo.name}
                          onChange={(e) => setGuardianInfo({ ...guardianInfo, name: e.target.value })}
                          placeholder="Enter householder name"
                          style={{ fontFamily: 'Lato, sans-serif' }}
                        />
                      </div>

                      <div>
                        <Label htmlFor="guardian-dob" value="Date of Birth" style={{ fontFamily: 'Lato, sans-serif' }} />
                        <TextInput
                          id="guardian-dob"
                          type="date"
                          value={guardianInfo.dob}
                          onChange={(e) => setGuardianInfo({ ...guardianInfo, dob: e.target.value })}
                          style={{ fontFamily: 'Lato, sans-serif' }}
                        />
                      </div>

                      <div>
                        <Label htmlFor="guardian-education" value="Education" style={{ fontFamily: 'Lato, sans-serif' }} />
                        <TextInput
                          id="guardian-education"
                          value={guardianInfo.education}
                          onChange={(e) => setGuardianInfo({ ...guardianInfo, education: e.target.value })}
                          placeholder="Enter education"
                          style={{ fontFamily: 'Lato, sans-serif' }}
                        />
                      </div>

                      <div>
                        <Label htmlFor="guardian-job" value="Job" style={{ fontFamily: 'Lato, sans-serif' }} />
                        <TextInput
                          id="guardian-job"
                          value={guardianInfo.job}
                          onChange={(e) => setGuardianInfo({ ...guardianInfo, job: e.target.value })}
                          placeholder="Enter job"
                          style={{ fontFamily: 'Lato, sans-serif' }}
                        />
                      </div>

                      <div>
                        <Label htmlFor="guardian-baptismDate" value="Baptism Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                        <TextInput
                          id="guardian-baptismDate"
                          type="date"
                          value={guardianInfo.baptismDate}
                          onChange={(e) => setGuardianInfo({ ...guardianInfo, baptismDate: e.target.value })}
                          style={{ fontFamily: 'Lato, sans-serif' }}
                        />
                      </div>

                      <div>
                        <Label htmlFor="guardian-firstCommunionDate" value="First Communion Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                        <TextInput
                          id="guardian-firstCommunionDate"
                          type="date"
                          value={guardianInfo.firstCommunionDate}
                          onChange={(e) => setGuardianInfo({ ...guardianInfo, firstCommunionDate: e.target.value })}
                          style={{ fontFamily: 'Lato, sans-serif' }}
                        />
                      </div>

                      <div>
                        <Label htmlFor="guardian-confirmationDate" value="Confirmation Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                        <TextInput
                          id="guardian-confirmationDate"
                          type="date"
                          value={guardianInfo.confirmationDate}
                          onChange={(e) => setGuardianInfo({ ...guardianInfo, confirmationDate: e.target.value })}
                          style={{ fontFamily: 'Lato, sans-serif' }}
                        />
                      </div>

                      <div>
                        <Label htmlFor="guardian-marriedDate" value="Married Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                        <TextInput
                          id="guardian-marriedDate"
                          type="date"
                          value={guardianInfo.marriedDate}
                          onChange={(e) => setGuardianInfo({ ...guardianInfo, marriedDate: e.target.value })}
                          disabled={!guardianInfo.married}
                          style={{ fontFamily: 'Lato, sans-serif' }}
                        />
                      </div>

                      <div>
                        <Label htmlFor="guardian-deathDate" value="Death Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                        <TextInput
                          id="guardian-deathDate"
                          type="date"
                          value={guardianInfo.deathDate}
                          onChange={(e) => setGuardianInfo({ ...guardianInfo, deathDate: e.target.value })}
                          style={{ fontFamily: 'Lato, sans-serif' }}
                        />
                      </div>

                      <div className="flex items-center gap-2">
                        <ToggleSwitch checked={guardianInfo.married} onChange={(checked) => setGuardianInfo({ ...guardianInfo, married: checked })} />
                        <Label htmlFor="guardian-married" value="Married" style={{ fontFamily: 'Lato, sans-serif' }} />
                      </div>

                      <div>
                        <Label htmlFor="guardian-contactNumbers" value="Contact Numbers (Optional)" style={{ fontFamily: 'Lato, sans-serif' }} />
                        <div className="space-y-2">
                          {guardianInfo.contactNumbers.map((contact, contactIndex) => (
                            <div key={contactIndex} className="flex gap-2">
                              <TextInput
                                value={contact}
                                onChange={(e) => {
                                  const newContacts = [...guardianInfo.contactNumbers];
                                  newContacts[contactIndex] = e.target.value;
                                  setGuardianInfo({ ...guardianInfo, contactNumbers: newContacts });
                                }}
                                placeholder="Enter contact number"
                                style={{ fontFamily: 'Lato, sans-serif' }}
                              />
                              {guardianInfo.contactNumbers.length > 0 && (
                                <Button
                                  onClick={() => {
                                    const newContacts = guardianInfo.contactNumbers.filter((_, i) => i !== contactIndex);
                                    setGuardianInfo({ ...guardianInfo, contactNumbers: newContacts });
                                  }}
                                  size="sm"
                                  color="failure"
                                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                >
                                  <HiX />
                                </Button>
                              )}
                            </div>
                          ))}
                          <Button
                            onClick={() => setGuardianInfo({ ...guardianInfo, contactNumbers: [...guardianInfo.contactNumbers, ''] })}
                            size="sm"
                            className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                            style={{ fontFamily: 'Lato, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                            <span style={{ display: 'flex', alignItems: 'center' }}>
                              <HiPlus className="mr-1" /> Add Contact
                            </span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-6">
                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                        Family Members
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                        Add family members (optional)
                      </p>
                    </div>
                    <Button
                      onClick={addMember}
                      size="sm"
                      className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                      style={{ fontFamily: 'Lato, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center' }}>
                        <HiPlus className="mr-2" /> Add Member
                      </span>
                    </Button>
                  </div>

                  <div className="space-y-6">
                    {familyMembers.map((member, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                        <div className="flex justify-between items-center mb-4">
                          <h5 className="font-semibold text-gray-700 dark:text-gray-300" style={{ fontFamily: 'Lato, sans-serif' }}>
                            Member {index + 1}
                          </h5>
                          {familyMembers.length > 1 && (
                            <Button
                              onClick={() => removeMember(index)}
                              size="sm"
                              color="failure"
                              style={{ fontFamily: 'Lato, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                              <span style={{ display: 'flex', alignItems: 'center' }}>
                                <HiX className="mr-1" /> Remove
                              </span>
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor={`name-${index}`} value="Name *" style={{ fontFamily: 'Lato, sans-serif' }} />
                            <TextInput
                              id={`name-${index}`}
                              value={member.name}
                              onChange={(e) => updateMember(index, 'name', e.target.value)}
                              placeholder="Enter name"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            />
                          </div>

                          <div>
                            <Label htmlFor={`dob-${index}`} value="Date of Birth" style={{ fontFamily: 'Lato, sans-serif' }} />
                            <TextInput
                              id={`dob-${index}`}
                              type="date"
                              value={member.dob}
                              onChange={(e) => updateMember(index, 'dob', e.target.value)}
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            />
                          </div>

                          <div>
                            <Label htmlFor={`education-${index}`} value="Education" style={{ fontFamily: 'Lato, sans-serif' }} />
                            <TextInput
                              id={`education-${index}`}
                              value={member.education}
                              onChange={(e) => updateMember(index, 'education', e.target.value)}
                              placeholder="Enter education"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            />
                          </div>

                          <div>
                            <Label htmlFor={`job-${index}`} value="Job" style={{ fontFamily: 'Lato, sans-serif' }} />
                            <TextInput
                              id={`job-${index}`}
                              value={member.job}
                              onChange={(e) => updateMember(index, 'job', e.target.value)}
                              placeholder="Enter job"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            />
                          </div>

                          <div>
                            <Label htmlFor={`baptismDate-${index}`} value="Baptism Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                            <TextInput
                              id={`baptismDate-${index}`}
                              type="date"
                              value={member.baptismDate}
                              onChange={(e) => updateMember(index, 'baptismDate', e.target.value)}
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            />
                          </div>

                          <div>
                            <Label htmlFor={`firstCommunionDate-${index}`} value="First Communion Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                            <TextInput
                              id={`firstCommunionDate-${index}`}
                              type="date"
                              value={member.firstCommunionDate}
                              onChange={(e) => updateMember(index, 'firstCommunionDate', e.target.value)}
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            />
                          </div>

                          <div>
                            <Label htmlFor={`confirmationDate-${index}`} value="Confirmation Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                            <TextInput
                              id={`confirmationDate-${index}`}
                              type="date"
                              value={member.confirmationDate}
                              onChange={(e) => updateMember(index, 'confirmationDate', e.target.value)}
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            />
                          </div>

                          <div>
                            <Label htmlFor={`marriedDate-${index}`} value="Married Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                            <TextInput
                              id={`marriedDate-${index}`}
                              type="date"
                              value={member.marriedDate}
                              onChange={(e) => updateMember(index, 'marriedDate', e.target.value)}
                              disabled={!member.married}
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            />
                          </div>

                          <div>
                            <Label htmlFor={`deathDate-${index}`} value="Death Date" style={{ fontFamily: 'Lato, sans-serif' }} />
                            <TextInput
                              id={`deathDate-${index}`}
                              type="date"
                              value={member.deathDate}
                              onChange={(e) => updateMember(index, 'deathDate', e.target.value)}
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            />
                          </div>

                          <div>
                            <Label htmlFor={`mother-${index}`} value="Mother" style={{ fontFamily: 'Lato, sans-serif' }} />
                            <Select
                              id={`mother-${index}`}
                              value={member.mother}
                              onChange={(e) => updateMember(index, 'mother', e.target.value)}
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              <option value="">Select Mother (Optional)</option>
                              {guardianInfo.name && <option value="guardian">{guardianInfo.name} (Householder)</option>}
                              {familyMembers.map((m, i) => {
                                if (i === index || !m.name.trim()) return null;
                                return (
                                  <option key={i} value={`member-${i}`}>
                                    {m.name}
                                  </option>
                                );
                              })}
                            </Select>
                            <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                              Note: You can edit relationships after creating the family
                            </p>
                          </div>

                          <div>
                            <Label htmlFor={`father-${index}`} value="Father" style={{ fontFamily: 'Lato, sans-serif' }} />
                            <Select
                              id={`father-${index}`}
                              value={member.father}
                              onChange={(e) => updateMember(index, 'father', e.target.value)}
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              <option value="">Select Father (Optional)</option>
                              {guardianInfo.name && <option value="guardian">{guardianInfo.name} (Householder)</option>}
                              {familyMembers.map((m, i) => {
                                if (i === index || !m.name.trim()) return null;
                                return (
                                  <option key={i} value={`member-${i}`}>
                                    {m.name}
                                  </option>
                                );
                              })}
                            </Select>
                            <p className="text-xs text-gray-500 mt-1" style={{ fontFamily: 'Lato, sans-serif' }}>
                              Note: You can edit relationships after creating the family
                            </p>
                          </div>

                          <div className="col-span-2 flex items-center gap-2">
                            <ToggleSwitch checked={member.married} onChange={(checked) => updateMember(index, 'married', checked)} />
                            <Label htmlFor={`married-${index}`} value="Married" style={{ fontFamily: 'Lato, sans-serif' }} />
                          </div>

                          <div className="col-span-2">
                            <Label value="Contact Numbers (Optional)" style={{ fontFamily: 'Lato, sans-serif' }} />
                            <div className="space-y-2">
                              {member.contactNumbers.map((contact, contactIndex) => (
                                <div key={contactIndex} className="flex gap-2">
                                  <TextInput
                                    value={contact}
                                    onChange={(e) => updateContactNumber(index, contactIndex, e.target.value)}
                                    placeholder="Enter contact number"
                                    style={{ fontFamily: 'Lato, sans-serif' }}
                                  />
                                  {member.contactNumbers.length > 0 && (
                                    <Button
                                      onClick={() => removeContactNumber(index, contactIndex)}
                                      size="sm"
                                      color="failure"
                                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    >
                                      <HiX />
                                    </Button>
                                  )}
                                </div>
                              ))}
                              <Button
                                onClick={() => addContactNumber(index)}
                                size="sm"
                                className="bg-[#B8860B] text-white hover:bg-[#9A7209]"
                                style={{ fontFamily: 'Lato, sans-serif', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              >
                                <span style={{ display: 'flex', alignItems: 'center' }}>
                                  <HiPlus className="mr-1" /> Add Contact
                                </span>
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="w-full pt-4 border-t">
              <Button
                onClick={handleAddOrEdit}
                disabled={loading || !familyName || !selectedUnit || (modalType === 'add' && !guardianInfo.name.trim())}
                className="w-full px-6 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px' }}
              >
                {loading ? 'Processing...' : modalType === 'add' ? 'Create Family' : 'Update Family'}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <PageMeta title="Families" description="Manage families" />
      <PageBreadcrumb pageTitle="Families" />
      <div className="space-y-6">
        <ComponentCard title="Families">
          <div className="flex flex-col md:flex-row w-full md:justify-start md:items-center gap-5">
            {(hasCreatePermission || isMainAdmin) && (
              <Button
                onClick={() => {
                  setOpenAddModal(true);
                  setModalType('add');
                  setFamilyName('');
                  setSelectedUnit('');
                  setGuardianInfo({
                    name: '',
                    dob: '',
                    education: '',
                    job: '',
                    baptismDate: '',
                    firstCommunionDate: '',
                    confirmationDate: '',
                    married: false,
                    marriedDate: '',
                    deathDate: '',
                    mother: '',
                    father: '',
                    contactNumbers: [],
                  });
                  setFamilyMembers([
                    {
                      name: '',
                      dob: '',
                      education: '',
                      job: '',
                      baptismDate: '',
                      firstCommunionDate: '',
                      confirmationDate: '',
                      married: false,
                      marriedDate: '',
                      deathDate: '',
                      mother: '',
                      father: '',
                      contactNumbers: [],
                    },
                  ]);
                }}
                className="px-6 bg-[#B8860B] text-white rounded-lg hover:bg-[#9A7209] transition-colors font-medium"
                style={{ fontFamily: 'Lato, sans-serif', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              >
                Add Family
              </Button>
            )}
            <div className="relative">
              <span className="absolute -translate-y-1/2 pointer-events-none left-4 top-1/2">
                <svg
                  className="fill-gray-500 dark:fill-gray-400"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M3.04175 9.37363C3.04175 5.87693 5.87711 3.04199 9.37508 3.04199C12.8731 3.04199 15.7084 5.87693 15.7084 9.37363C15.7084 12.8703 12.8731 15.7053 9.37508 15.7053C5.87711 15.7053 3.04175 12.8703 3.04175 9.37363ZM9.37508 1.54199C5.04902 1.54199 1.54175 5.04817 1.54175 9.37363C1.54175 13.6991 5.04902 17.2053 9.37508 17.2053C11.2674 17.2053 13.003 16.5344 14.357 15.4176L17.177 18.238C17.4699 18.5309 17.9448 18.5309 18.2377 18.238C18.5306 17.9451 18.5306 17.4703 18.2377 17.1774L15.418 14.3573C16.5365 13.0033 17.2084 11.2669 17.2084 9.37363C17.2084 5.04817 13.7011 1.54199 9.37508 1.54199Z"
                    fill=""
                  />
                </svg>
              </span>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                type="text"
                placeholder="Search families..."
                className="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-200 bg-transparent py-2.5 pl-12 pr-14 text-sm text-gray-800 shadow-theme-xs placeholder:text-gray-400 focus:border-[#B8860B] focus:outline-none focus:ring focus:ring-[#B8860B]/10 dark:border-gray-800 dark:bg-gray-900 dark:bg-white/[0.03] dark:text-white/90 dark:placeholder:text-white/30 dark:focus:border-[#B8860B] xl:w-[430px]"
                style={{ fontFamily: 'Lato, sans-serif' }}
              />
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.05] dark:bg-white/[0.03] mt-6">
            <div className="max-w-full overflow-x-auto">
              <div className="min-w-[1000px]">
                <Table>
                  <TableHeader className="border-b border-gray-100 dark:border-white/[0.05]">
                    <TableRow>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Family Name
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Unit
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Created At
                      </TableCell>
                      <TableCell
                        isHeader
                        className="px-5 py-3 font-medium text-gray-500 text-start text-theme-xs dark:text-gray-400"
                        style={{ fontFamily: 'Playfair Display, serif' }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHeader>

                  <TableBody className="divide-y divide-gray-100 dark:divide-white/[0.05]">
                    {tableData.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={4}
                          className="px-5 py-8 text-center text-gray-500 dark:text-gray-400"
                          style={{ fontFamily: 'Lato, sans-serif' }}
                        >
                          {loading ? 'Loading...' : 'No families found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      tableData.map((family) => (
                        <TableRow key={family._id}>
                          <div
                            className="contents cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-md transition-all duration-200 group"
                            onClick={() => navigate(`/families/${family._id}`)}
                          >
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400 group-hover:border-l-4 group-hover:border-[#B8860B] group-hover:text-gray-900 dark:group-hover:text-white transition-all"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {family.familyName}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {typeof family.unit === 'object' && family.unit ? `${family.unit.name} (${family.unit.unitNumber})` : 'N/A'}
                            </TableCell>
                            <TableCell
                              className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors"
                              style={{ fontFamily: 'Lato, sans-serif' }}
                            >
                              {dayjs(family.createdAt).format('DD MMM YYYY')}
                            </TableCell>
                            <TableCell className="px-4 py-3 text-gray-500 text-theme-sm dark:text-gray-400">
                              <div onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                                <Popover
                                  placement="top"
                                  aria-labelledby="default-popover"
                                  content={
                                    <div className="py-2 px-3">
                                      {(hasDeletePermission || isMainAdmin) && (
                                        <div
                                          className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 cursor-pointer"
                                          onClick={() => {
                                            setSelectedFamily(family._id);
                                            setOpenModal(true);
                                          }}
                                          style={{ fontFamily: 'Lato, sans-serif' }}
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="lucide lucide-trash-2"
                                          >
                                            <path d="M3 6h18" />
                                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                                            <line x1="10" x2="10" y1="11" y2="17" />
                                            <line x1="14" x2="14" y1="11" y2="17" />
                                          </svg>
                                          Delete
                                        </div>
                                      )}

                                      {(hasUpdatePermission || isMainAdmin) && (
                                        <div
                                          className="flex items-center gap-3 px-3 py-2 font-medium text-gray-700 rounded-lg group text-theme-sm hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-white/5 dark:hover:text-gray-300 cursor-pointer"
                                          onClick={() => handleEdit(family)}
                                          style={{ fontFamily: 'Lato, sans-serif' }}
                                        >
                                          <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="lucide lucide-pencil"
                                          >
                                            <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5 5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                                            <path d="m15 5 4 4" />
                                          </svg>
                                          Edit
                                        </div>
                                      )}
                                    </div>
                                  }
                                >
                                  <button
                                    className="flex items-center justify-center w-10 h-10 text-gray-700 rounded-lg z-99999 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800 transition-colors"
                                    style={{ fontFamily: 'Lato, sans-serif' }}
                                  >
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path
                                        fillRule="evenodd"
                                        clipRule="evenodd"
                                        d="M5.99902 10.4951C6.82745 10.4951 7.49902 11.1667 7.49902 11.9951V12.0051C7.49902 12.8335 6.82745 13.5051 5.99902 13.5051C5.1706 13.5051 4.49902 12.8335 4.49902 12.0051V11.9951C4.49902 11.1667 5.1706 10.4951 5.99902 10.4951ZM17.999 10.4951C18.8275 10.4951 19.499 11.1667 19.499 11.9951V12.0051C19.499 12.8335 18.8275 13.5051 17.999 13.5051C17.1706 13.5051 16.499 12.8335 16.499 12.0051V11.9951C16.499 11.1667 17.1706 10.4951 17.999 10.4951ZM13.499 11.9951C13.499 11.1667 12.8275 10.4951 11.999 10.4951C11.1706 10.4951 10.499 11.1667 10.499 11.9951V12.0051C10.499 12.8335 11.1706 13.5051 11.999 13.5051C12.8275 13.5051 13.499 12.8335 13.499 12.0051V11.9951Z"
                                        fill="currentColor"
                                      />
                                    </svg>
                                  </button>
                                </Popover>
                              </div>
                            </TableCell>
                          </div>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 px-4 py-3 border-t border-gray-200 dark:border-white/[0.05]">
              <div className="text-sm text-gray-500 dark:text-gray-400" style={{ fontFamily: 'Lato, sans-serif' }}>
                Showing {(pagination.page - 1) * pagination.limit + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of{' '}
                {pagination.total} results
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  color="light"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={!pagination.hasPrevPage || loading}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (pagination.totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNum = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNum = pagination.totalPages - 4 + i;
                    } else {
                      pageNum = pagination.page - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        color={pagination.page === pageNum ? 'warning' : 'light'}
                        onClick={() => setCurrentPage(pageNum)}
                        disabled={loading}
                        style={{ fontFamily: 'Lato, sans-serif', minWidth: '40px' }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  size="sm"
                  color="light"
                  onClick={() => setCurrentPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={!pagination.hasNextPage || loading}
                  style={{ fontFamily: 'Lato, sans-serif' }}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </ComponentCard>
      </div>
    </>
  );
}
